import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FileHelpers } from '../../core/utilities/file.helpers';
import * as _ from 'lodash';
import { fromEvent, Subscription } from 'rxjs';
import { DroppableDirective } from '../../core/visual/droppable/droppable.directive';
import { NgIf } from '@angular/common';
import { PdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';

@Component({
    selector: 'app-upload-fax',
    template: ` <div class="row" (paste)="onFilePaste($event)">
    <div class="upload-wrapper col-lg-6">
      <input
        #fileInput
        [name]="name"
        id="{{ _id }}-input"
        class="file-input"
        type="file"
        (change)="fileChangeEvent($event)"
        placeholder="Choose a file..."
      />
      <label class="file-input-label" for="{{ _id }}-input" title="Choose a file...">
        <i class="material-icons">attach_file</i>
        Select, drag or paste file here
      </label>
      <div class="file-drop-area" appDroppable (dropped)="dropFile($event)">
        <div>Drop File Here</div>
      </div>
    </div>
    <div class="preview-attachment col-lg-6" *ngIf="previewFileName || previewPdf">
      <div *ngIf="previewFileName">
        {{ previewFileName }}
        <span (click)="removeAttachment()" class="remove-attachment" title="Remove attachment">
          <i class="material-icons">close</i>
        </span>
      </div>
      <div #previewContainer class="inner-container" [style.height]="containerHeight + 'px'">
        <div style="display: inline-block; width: 100%; padding: 5px; background-color: gray; height: 100%;">
          <img [src]="previewUrl" *ngIf="previewUrl" alt="File Preview" />
          <app-pdf-viewer
            [src]="previewPdf"
            *ngIf="previewPdf"
            (afterLoadComplete)="afterPdfLoaded()"
            class="preview-holder"
          ></app-pdf-viewer>
        </div>
      </div>
    </div>
  </div>`,
    styleUrls: ['./upload-fax.component.scss'],
    standalone: true,
    imports: [
        DroppableDirective,
        NgIf,
        PdfViewerComponent,
    ],
})
export class UploadFaxComponent implements OnInit, OnChanges, OnDestroy {
  @Input() id: string;
  @Input() name: string;
  @Input() allowImages: boolean;

  _id: string;

  file?: File;
  previewUrl?: string;
  @Input()
  previewPdf?: string;
  previewFileName?: string;

  containerHeight = 100;
  resizeSubscription: Subscription;
  loadPdfTimeout: any;
  resizeTimeout: any;

  @ViewChild('fileInput', { static: true }) fileInput: any;
  @ViewChild('previewContainer') previewContainer: ElementRef;

  @Output() valueChanged = new EventEmitter<File>();
  @Output() validationError = new EventEmitter<string>();

  constructor() {
    this.resizeSubscription = fromEvent(window, 'resize').subscribe(event => {
      console.log('On resize fax upload');
      this.updateSize();
    });
  }

  afterPdfLoaded() {
    window.dispatchEvent(new Event('resize'));
  }

  setId(): void {
    if (this.id) {
      this._id = this.id;
    } else if (this.name) {
      this._id = this._id = _.uniqueId(`${this.name}_`);
    } else {
      this._id = _.uniqueId('id_');
    }
  }

  fileChangeEvent(fileInput: any) {
    const files = fileInput.target.files;
    if (files.length > 0) {
      const file: File = files[0];
      this.validateAndProceedFile(file);
    }
  }

  onFilePaste(event: ClipboardEvent) {
    for (let i = 0; i < (event.clipboardData?.items?.length || 0); i++) {
      const item = event.clipboardData!.items[i];
      console.log('Item: ' + item.type);
      if (item.type.indexOf('image') > -1) {
        this.validateAndProceedFile(item.getAsFile()!);
      } else {
        console.log('Discarding image paste data');
      }
    }
  }

  dropFile(event: DragEvent) {
    console.log('drop event');

    const files = event.dataTransfer?.files || [];

    if (files.length > 0) {
      const file: File = files[0];
      this.validateAndProceedFile(file);
    }
  }

  validateAndProceedFile(file: File) {
    if (FileHelpers.isSupportedFile(file, this.allowImages ? 'fax_merge' : 'fax')) {
      if (FileHelpers.isSupportedFaxFileSize(file)) {
        this.file = file;
        this.valueChanged.emit(file);
        this.previewFile(file);
      } else {
        this.validationError.emit('File size must be less then 10MB');
      }
    } else {
      this.validationError.emit(
        'The file type that you uploaded is not accepted. Please use a file type from the supported list.',
      );
    }
  }

  removeAttachment() {
    this.file = undefined;
    this.previewUrl = undefined;
    this.previewPdf = undefined;
    this.previewFileName = undefined;
    this.fileInput.nativeElement.value = '';

    this.valueChanged.emit(this.file);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(changes);
  }

  ngOnInit(): void {
    this.setId();
    window.dispatchEvent(new Event('resize'));
  }

  updateSize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      if (this.previewContainer) {
        this.containerHeight = this.previewContainer.nativeElement.clientWidth * 1.4142 + 10;
      }
    }, 300);
  }

  ngOnDestroy(): void {
    if (this.resizeSubscription) {
      this.resizeSubscription?.unsubscribe();
    }
  }

  private previewFile(file: File) {
    this.previewFileName = file.name;
    this.previewUrl = undefined;
    this.previewPdf = undefined;

    if (file.type.indexOf('image') > -1) {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        this.previewUrl = e.target.result;
        window.dispatchEvent(new Event('resize'));
      };

      reader.readAsDataURL(file);
    }

    if (file.type.indexOf('pdf') > -1) {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        clearTimeout(this.loadPdfTimeout);
        this.loadPdfTimeout = setTimeout(() => {
          this.previewPdf = e.target.result;
          window.dispatchEvent(new Event('resize'));
        }, 500);
      };

      reader.readAsArrayBuffer(file);
    }
  }
}
